import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowLeft, ArrowRight, Vote, MousePointer, Award } from 'lucide-react'
import { checkVoteEligibility, getCompetitionStatus } from '../lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'

const ALLOWED_DOMAINS = ['scsstudent.org', 'sierracanyonschool.org']
const ALLOWED_EMAILS = ['garrett@garrettjohnlaw.com', 'ddinstman@sierracanyonschool.org']

function isValidSchoolEmail(email: string): boolean {
  const normalizedEmail = email.trim().toLowerCase()

  // Check if email is in allowed emails list (e.g., for testing)
  if (ALLOWED_EMAILS.includes(normalizedEmail)) {
    return true
  }

  // Check if domain is in allowed domains
  const domain = normalizedEmail.split('@')[1]
  return ALLOWED_DOMAINS.includes(domain)
}

export default function VoteRequestPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isCheckingMode, setIsCheckingMode] = useState(true)

  // Check voting mode on load
  useEffect(() => {
    async function checkVotingMode() {
      const result = await getCompetitionStatus()
      if (result.data?.votingMode === 'elo') {
        // Redirect to ELO voting page
        navigate('/vote/elo', { replace: true })
      } else {
        setIsCheckingMode(false)
      }
    }
    checkVotingMode()
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmedEmail = email.trim().toLowerCase()

    if (!trimmedEmail) {
      setError('Please enter your email address')
      return
    }

    if (!isValidSchoolEmail(trimmedEmail)) {
      setError('Please use your Sierra Canyon student email (@scsstudent.org)')
      return
    }

    setIsSubmitting(true)

    try {
      const result = await checkVoteEligibility(trimmedEmail)

      if (result.error) {
        setError(result.error)
        setIsSubmitting(false)
        return
      }

      // Navigate to voting page with email and photos
      navigate('/vote/classic', {
        state: {
          email: trimmedEmail,
          photos: result.data?.photos ?? [],
        },
      })
    } catch {
      setError('Something went wrong. Please try again.')
      setIsSubmitting(false)
    }
  }

  // Show loading while checking voting mode
  if (isCheckingMode) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="border-accent mx-auto h-10 w-10 animate-spin rounded-full border-2 border-t-transparent" />
          <p className="text-foreground-muted mt-4">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background min-h-screen px-4 py-8">
      <div className="mx-auto max-w-lg">
        <Link
          to="/"
          className="text-foreground-muted hover:text-foreground mb-8 inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
          <div className="space-y-3">
            <h1 className="text-foreground font-display text-3xl font-bold md:text-4xl">Vote for Your Favorites</h1>
            <p className="text-foreground-muted text-lg">
              Enter your Sierra Canyon email to vote. You'll be able to pick your top 3 favorite photos.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setError(null)
              }}
              placeholder="you@scsstudent.org"
              hint="Use your @scsstudent.org email"
              error={error ?? undefined}
              disabled={isSubmitting}
              className="text-lg"
            />

            <Button
              type="submit"
              size="lg"
              isLoading={isSubmitting}
              disabled={isSubmitting || !email.trim()}
              className="w-full"
            >
              Continue to Vote
              <ArrowRight className="h-5 w-5" />
            </Button>
          </form>

          {/* How it works */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-surface border-border rounded-2xl border p-6"
          >
            <h3 className="text-foreground mb-4 font-semibold">How it works</h3>
            <div className="space-y-4">
              {[
                { icon: Vote, text: 'Enter your Sierra Canyon school email' },
                { icon: MousePointer, text: 'Browse through all the submitted photos' },
                { icon: Award, text: 'Pick your top 3 favorites to vote' },
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="bg-surface-hover flex h-8 w-8 shrink-0 items-center justify-center rounded-lg">
                    <span className="text-accent text-sm font-semibold">{index + 1}</span>
                  </div>
                  <span className="text-foreground-muted text-sm">{step.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}
